/**
 * Factory for creating typed React hooks that subscribe to EventEmitter events.
 *
 * This provides a clean, declarative way to respond to events in React components
 * without the race conditions of useEffect-based state watching.
 *
 * @example
 * // Create a typed hook for auth events
 * const useAuthEvent = createEventHook<AuthEvents>(() => useAuthEmitter());
 *
 * // Use in components
 * useAuthEvent('signed_out', async () => {
 *   await dropAllDatabases();
 * });
 *
 * useAuthEvent('user_changed', (prev, next) => {
 *   console.log(`User changed from ${prev} to ${next}`);
 * });
 */

import type { EventEmitter } from "eventemitter3";
import { useEffect, useRef } from "react";

/**
 * Creates a typed hook for subscribing to events from an EventEmitter.
 *
 * The returned hook uses a stable callback ref pattern to avoid re-subscribing
 * when the callback function reference changes (common with inline callbacks).
 *
 * @param useEmitter - Hook that returns the EventEmitter instance
 * @returns A hook function for subscribing to events
 *
 * @example
 * // In auth-store:
 * const useAuthEvent = createEventHook<AuthEvents>(() => useAuthStore.getState().emitter);
 *
 * // In components:
 * useAuthEvent('signed_out', () => cleanup());
 * useAuthEvent('user_changed', (prev, next) => handleMerge(prev, next));
 */
export function createEventHook<
  Events extends { [K in keyof Events]: unknown[] },
>(useEmitter: () => EventEmitter<Events>) {
  // Constrain E to string keys only (EventEmitter requires string | symbol event names)
  return function useEvent<E extends keyof Events & string>(
    event: E,
    callback: (...args: Events[E]) => void
  ): void {
    const emitter = useEmitter();

    // Stable callback ref to avoid re-subscribing on callback changes.
    // This is critical: inline callbacks like () => foo() create new
    // function references on every render, but we don't want to
    // unsubscribe/resubscribe each time.
    const callbackRef = useRef(callback);
    callbackRef.current = callback;

    useEffect(() => {
      // Handler must be cast to satisfy EventEmitter's generic constraints
      // biome-ignore lint/suspicious/noExplicitAny: EventEmitter generic constraints require this
      const handler = ((...args: Events[E]) => callbackRef.current(...args)) as
        // biome-ignore lint/suspicious/noExplicitAny: EventEmitter generic constraints require this
        unknown as Parameters<EventEmitter<Events>["on"]>[1];
      // Event name cast needed: EventEmitter requires EventNames<Events> type
      // which is overly restrictive for our use case
      // biome-ignore lint/suspicious/noExplicitAny: EventEmitter requires specific event name type
      emitter.on(event as any, handler);
      return () => {
        // biome-ignore lint/suspicious/noExplicitAny: EventEmitter requires specific event name type
        emitter.off(event as any, handler);
      };
    }, [emitter, event]);
  };
}
